import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import BottomNav from '../components/BottomNav';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../App';
import { TERMS_PDF, PRIVACY_PDF } from '../utils/consent';
import { getPushState, subscribeToPush, unsubscribeFromPush } from '../utils/push';

const Stars = ({ score }) => {
  const full = Math.floor(score);
  return (
    <div className="stars">
      {[1,2,3,4,5].map(i=>(
        <span key={i} className={`star ${i<=full?'':'empty'}`}>★</span>
      ))}
      <span style={{fontSize:12,color:'var(--text-secondary)',marginLeft:4}}>{Number(score).toFixed(1)}</span>
    </div>
  );
};

// ── Session T: per-environment agency staffing terms ────────────────────────
// The client's own staffing terms — a COMMERCIAL document from the agency,
// deliberately kept apart from the SHIFTon T&Cs and Privacy Policy below,
// which are Responsible Party documents and stay identical on every branch.
//
// Ops: upload the client's PDF to Cloudinary, set REACT_APP_AGENCY_TERMS_URL on
// that client's FRONTEND service, redeploy. Unset — demo, or any client who has
// not sent one — renders nothing at all.
//
// REACT_APP_* bakes in at BUILD time: setting the variable does nothing until a
// redeploy. https:// is required; anything else is ignored with a console warning
// so a typo shows up as a diagnosable message rather than a silently missing link.
const AGENCY_TERMS_URL = (() => {
  const raw = (process.env.REACT_APP_AGENCY_TERMS_URL || '').trim();
  if (!raw) return '';
  if (!/^https:\/\//i.test(raw)) {
    console.warn('REACT_APP_AGENCY_TERMS_URL must start with https:// — staffing terms link not rendered.');
    return '';
  }
  return raw;
})();

// Same idiom as AgentDashboard's export brand string: BRAND_PREFIX carries a
// trailing space ("Bosse " + "Events"), so concatenate then trim. The two-tone
// wordmark's ||'SHIFT' / ||'on' fallbacks are wrong for a plain text string.
const BRAND = (`${process.env.REACT_APP_BRAND_PREFIX || ''}${process.env.REACT_APP_BRAND_SUFFIX || ''}`.trim()) || 'SHIFTon';

// BOSSE Step 1 — dropdown option sets
const RACE_OPTIONS = ['Black', 'Coloured', 'Indian', 'White', 'Prefer not to say'];
// Session Z — KEEP IN SYNC with server.js (GENDER_OPTIONS, TSHIRT_OPTIONS,
// PANTS_MIN/PANTS_MAX). The server 400s on anything outside these, so a value
// that renders here but not there produces an error the staff member cannot fix.
const GENDER_OPTIONS = ['Male', 'Female', 'Other', 'Prefer not to say'];
const TSHIRT_OPTIONS = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
const SA_BANKS = ['ABSA', 'African Bank', 'Capitec', 'Discovery Bank', 'FNB', 'Investec', 'Nedbank', 'Standard Bank', 'TymeBank', 'Other'];

// Profile completeness — keep this field list in sync with the copy in BottomNav.js
// Session V (W2): demographics are NOT part of completeness. Height, weight and
// race are POPIA consent-gated and genuinely optional — race at least offers
// "Prefer not to say", height and weight have no opt-out at all — so requiring
// them left a permanent nag badge on the Account tab until staff disclosed
// their body measurements and race. A badge that only clears on disclosure is
// pressure, and consent under pressure is not consent.
//
// Session AA: t-shirt size and pants size ADDED. This is not a reversal of the
// above — those two are ORDINARY personal information with no consent gate, so
// there is no consent for a badge to put under pressure. They sit on the same
// footing as the photo: an agency operational need (a uniform cannot be ordered
// without them) rather than something the staff member is being asked to
// disclose about themselves.
// ⚠️ `gender` is deliberately NOT here. It has no stated operational purpose
// yet — it was collected because the intake form asked for it. Requiring a
// field nobody can justify is the weakest possible position under POPIA
// minimisation. Revisit only if a real use appears.
//
// Completeness is: the photo, the five bank fields (without them the person
// cannot be paid), and the two uniform sizes.
export const isProfileComplete = p => !!(
  p && p.picture &&
  p.bank_name && p.account_holder && p.account_number && p.branch_code && p.account_type &&
  p.tshirt_size && p.pants_size
);

export default function Account() {
  const { user: authUser, logout } = useAuth();
  // Session AH — push notifications. State is read from the BROWSER, not from
  // the server: the person may have revoked permission in phone settings, or
  // be on a different device from the one they enabled it on, and the server
  // row would still say "on". The browser is the only honest source.
  const [pushState, setPushState] = useState('off');
  const [pushBusy, setPushBusy] = useState(false);
  const [pushError, setPushError] = useState('');
  useEffect(() => { getPushState().then(setPushState); }, []);
  const { dark, toggle } = useTheme();
  const [profile, setProfile] = useState(null);
  const [saved, setSaved] = useState(false);
  const [picUploading, setPicUploading] = useState(false);
  const [picError, setPicError] = useState('');
  const [editingPhone, setEditingPhone] = useState(false);
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [phoneSaving, setPhoneSaving] = useState(false);
  const fileInputRef = useRef(null);

  // BOSSE Step 1 — My details + Banking details
  const [details, setDetails] = useState({ height_cm: '', weight_kg: '', race: '' });
  const [consent, setConsent] = useState(false);
  const [detailsSaving, setDetailsSaving] = useState(false);
  const [detailsError, setDetailsError] = useState('');
  const [bank, setBank] = useState({ bank_name: '', account_holder: '', account_number: '', branch_code: '', account_type: '' });
  const [bankSaving, setBankSaving] = useState(false);
  const [bankError, setBankError] = useState('');
  // Session Z: uniform sizing + gender. Deliberately its OWN card and its own
  // save, separate from My Details — those three fields are POPIA consent-gated
  // and these are not. Folding them into the consent card would either demand
  // consent for a t-shirt size or imply the consent box covers more than it does.
  const [sizing, setSizing] = useState({ gender: '', tshirt_size: '', pants_size: '' });
  const [sizingSaving, setSizingSaving] = useState(false);
  const [sizingError, setSizingError] = useState('');
  const [bannerDismissed, setBannerDismissed] = useState(() => sessionStorage.getItem('es_profile_banner') === 'dismissed');
  // Step 3.5 Pass B: sections flip to read-only after a save, with a bordered
  // "Edit details" button to reopen. A section with nothing saved yet starts
  // open so a new staff member can type straight away.
  const [detailsEditing, setDetailsEditing] = useState(true);
  const [bankEditing, setBankEditing] = useState(true);
  const [sizingEditing, setSizingEditing] = useState(true);

  useEffect(() => {
    axios.get('/api/staff/me').then(r => {
      setProfile(r.data);
      setDetails({
        height_cm: r.data.height_cm ?? '',
        weight_kg: r.data.weight_kg ?? '',
        race: r.data.race ?? '',
      });
      setConsent(Number(r.data.profile_consent) === 1);
      setBank({
        bank_name: r.data.bank_name ?? '',
        account_holder: r.data.account_holder ?? '',
        account_number: r.data.account_number ?? '',
        branch_code: r.data.branch_code ?? '',
        account_type: r.data.account_type ?? '',
      });
      setSizing({
        gender: r.data.gender ?? '',
        tshirt_size: r.data.tshirt_size ?? '',
        pants_size: r.data.pants_size ?? '',
      });
      // Step 3.5 Pass B: anything already saved → section starts read-only
      setDetailsEditing(!(r.data.height_cm || r.data.weight_kg || r.data.race));
      setBankEditing(!(r.data.bank_name || r.data.account_holder || r.data.account_number || r.data.branch_code || r.data.account_type));
      setSizingEditing(!(r.data.gender || r.data.tshirt_size || r.data.pants_size));
    });
  }, []);

  const saveDetails = async () => {
    setDetailsError('');
    const hasValue = details.height_cm !== '' || details.weight_kg !== '' || details.race !== '';
    if (hasValue && !consent) { setDetailsError('Please tick the consent box to save these details.'); return; }
    setDetailsSaving(true);
    try {
      const r = await axios.patch('/api/staff/me/profile', {
        height_cm: details.height_cm,
        weight_kg: details.weight_kg,
        race: details.race,
        profile_consent: consent ? 1 : 0,
      });
      setProfile(p => ({ ...p, ...r.data }));
      setSaved(true); setTimeout(() => setSaved(false), 3000);
      setDetailsEditing(false); // Step 3.5 Pass B: flip to read-only + "Edit details"
    } catch (e) {
      setDetailsError(e.response?.data?.error || 'Failed to save details');
    }
    setDetailsSaving(false);
  };

  const saveBank = async () => {
    setBankError('');
    setBankSaving(true);
    try {
      const r = await axios.patch('/api/staff/me/profile', {
        bank_name: bank.bank_name,
        account_holder: bank.account_holder,
        account_number: bank.account_number,
        branch_code: bank.branch_code,
        account_type: bank.account_type,
      });
      setProfile(p => ({ ...p, ...r.data }));
      setSaved(true); setTimeout(() => setSaved(false), 3000);
      setBankEditing(false); // Step 3.5 Pass B: flip to read-only + "Edit details"
    } catch (e) {
      setBankError(e.response?.data?.error || 'Failed to save banking details');
    }
    setBankSaving(false);
  };

  // Session Z: no consent flag in this payload — these are ordinary personal
  // information and the server keeps them outside the demographics gate.
  const saveSizing = async () => {
    setSizingError('');
    setSizingSaving(true);
    try {
      const r = await axios.patch('/api/staff/me/profile', {
        gender: sizing.gender,
        tshirt_size: sizing.tshirt_size,
        pants_size: sizing.pants_size,
      });
      setProfile(p => ({ ...p, ...r.data }));
      setSaved(true); setTimeout(() => setSaved(false), 3000);
      setSizingEditing(false);
    } catch (e) {
      setSizingError(e.response?.data?.error || 'Failed to save sizing details');
    }
    setSizingSaving(false);
  };

  // Step 3.5 Pass B: opening/cancelling an edit re-syncs the drafts from the
  // saved profile so a stale half-edit never lingers in the fields.
  const openDetailsEdit = () => {
    setDetails({ height_cm: profile.height_cm ?? '', weight_kg: profile.weight_kg ?? '', race: profile.race ?? '' });
    setConsent(Number(profile.profile_consent) === 1);
    setDetailsError('');
    setDetailsEditing(true);
  };
  const cancelDetailsEdit = () => {
    setDetails({ height_cm: profile.height_cm ?? '', weight_kg: profile.weight_kg ?? '', race: profile.race ?? '' });
    setConsent(Number(profile.profile_consent) === 1);
    setDetailsError('');
    setDetailsEditing(false);
  };
  const openBankEdit = () => {
    setBank({ bank_name: profile.bank_name ?? '', account_holder: profile.account_holder ?? '', account_number: profile.account_number ?? '', branch_code: profile.branch_code ?? '', account_type: profile.account_type ?? '' });
    setBankError('');
    setBankEditing(true);
  };
  const cancelBankEdit = () => {
    setBank({ bank_name: profile.bank_name ?? '', account_holder: profile.account_holder ?? '', account_number: profile.account_number ?? '', branch_code: profile.branch_code ?? '', account_type: profile.account_type ?? '' });
    setBankError('');
    setBankEditing(false);
  };
  const syncSizing = () => setSizing({ gender: profile.gender ?? '', tshirt_size: profile.tshirt_size ?? '', pants_size: profile.pants_size ?? '' });
  const openSizingEdit = () => { syncSizing(); setSizingError(''); setSizingEditing(true); };
  const cancelSizingEdit = () => { syncSizing(); setSizingError(''); setSizingEditing(false); };

  // Step 3.5 Pass B: read-only value row + the bordered "Edit details" button
  const RoRow = ({ label, value }) => (
    <div style={{display:'flex',justifyContent:'space-between',gap:12,padding:'8px 0',borderBottom:'1px solid var(--border-light)',fontSize:13}}>
      <span style={{color:'var(--text-secondary)'}}>{label}</span>
      <span style={{color:'var(--text-primary)',fontWeight:600,textAlign:'right'}}>{value || '—'}</span>
    </div>
  );
  const EditBtn = ({ onClick }) => (
    <button onClick={onClick}
      style={{width:'100%',marginTop:12,padding:'10px',borderRadius:8,border:'1.5px solid var(--border)',background:'transparent',color:'var(--text-primary)',fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:'var(--font)'}}>
      Edit details
    </button>
  );

  const dismissBanner = () => {
    sessionStorage.setItem('es_profile_banner', 'dismissed');
    setBannerDismissed(true);
  };

  const uploadPic = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPicError('');
    // Session C: iOS Safari sometimes hands over a stale/empty file (iCloud
    // photo not downloaded to the device). Catch it before wasting an upload.
    if (file.size === 0) {
      setPicError("That photo couldn't be read from your device — open it in Photos first, then try again.");
      e.target.value = '';
      return;
    }
    setPicUploading(true);
    try {
      const formData = new FormData();
      formData.append('picture', file);
      const { data } = await axios.patch('/api/staff/me/picture', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setProfile(p => ({ ...p, picture: data.picture }));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      // Was `catch {}` — a failed upload showed NOTHING (audit H2). Surface
      // the server's message (no-file guard, 10MB limit, type filter, or the
      // truncated-body 400) so the person knows what happened and what to do.
      setPicError(err.response?.data?.error || 'Upload failed — please check your connection and try again.');
    }
    e.target.value = ''; // allow re-selecting the same photo to retry
    setPicUploading(false);
  };

  const savePhone = async () => {
    setPhoneError('');
    if (!phoneInput.trim()) { setPhoneError('Please enter a phone number'); return; }
    setPhoneSaving(true);
    try {
      const r = await axios.patch('/api/staff/me/phone', { phone: phoneInput.trim() });
      setProfile(p => ({ ...p, phone: r.data.phone }));
      setEditingPhone(false);
      setPhoneInput('');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setPhoneError(e.response?.data?.error || 'Failed to update number');
    }
    setPhoneSaving(false);
  };

  if (!profile) return <div className="loading" style={{paddingTop:'40vh'}}>Loading...</div>;

  return (
    <div className="app-shell">
      <div className="screen">
        <div className="topbar">
          <h2 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.3px' }}>Your Account</h2>
        </div>

        {!isProfileComplete(profile) && !bannerDismissed && (
          <div style={{margin:'0 16px 12px',padding:'12px 14px',background:'rgba(234,179,8,0.1)',border:'1px solid rgba(234,179,8,0.35)',borderRadius:8,display:'flex',alignItems:'flex-start',gap:10}}>
            <div style={{flex:1,fontSize:13,color:'var(--text-primary)',lineHeight:1.5}}>
              <strong>Complete your profile</strong> — agencies use this info to book you for shifts.
            </div>
            <span onClick={dismissBanner} style={{cursor:'pointer',fontSize:16,color:'var(--text-secondary)',lineHeight:1,padding:'0 2px'}} title="Dismiss">✕</span>
          </div>
        )}

        <div className="card">
          <div style={{textAlign:'center',marginBottom:16}}>
            <div
              onClick={()=>fileInputRef.current?.click()}
              style={{width:72,height:72,borderRadius:'50%',background:'var(--accent)',margin:'0 auto 10px',display:'flex',alignItems:'center',justifyContent:'center',fontSize:26,fontWeight:700,color:'#fff',cursor:'pointer',overflow:'hidden',position:'relative',border:'3px solid var(--bg-card)',boxShadow:'0 2px 10px rgba(0,0,0,0.15)'}}
              title="Tap to change photo"
            >
              {profile.picture
                ? <img src={profile.picture} alt="Profile" style={{width:'100%',height:'100%',objectFit:'cover'}}/>
                : (picUploading ? '...' : (profile.name||'S').charAt(0).toUpperCase())
              }
              <div style={{position:'absolute',bottom:0,left:0,right:0,background:'rgba(0,0,0,0.35)',fontSize:9,color:'#fff',fontWeight:600,padding:'3px 0',textAlign:'center',letterSpacing:'0.3px'}}>
                {picUploading ? 'Uploading...' : 'Edit'}
              </div>
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" style={{display:'none'}} onChange={uploadPic}/>
            <div style={{fontSize:11,color:'var(--text-muted)',lineHeight:1.5,margin:'0 auto 10px',maxWidth:280}}>
              Use a clear head-and-shoulders photo on a plain white background — agencies use this for event staffing sheets.
            </div>
            {picError && (
              <div style={{fontSize:12,color:'var(--danger)',lineHeight:1.5,margin:'0 auto 10px',maxWidth:280,padding:'8px 12px',background:'rgba(var(--danger-rgb),0.08)',border:'1px solid var(--danger)',borderRadius:8}}>
                ⚠️ {picError}
              </div>
            )}
            <div style={{fontWeight:700,fontSize:16,color:'var(--text-primary)'}}>{profile.name}</div>
            <div style={{fontSize:12,color:'var(--text-secondary)',marginTop:2}}>{profile.email}</div>
            {!editingPhone ? (
              <div style={{fontSize:12,color:'var(--text-secondary)',marginTop:4,display:'flex',alignItems:'center',justifyContent:'center',gap:6}}>
                <span>📱 {profile.phone || 'No number saved'}</span>
                <span
                  onClick={() => { setPhoneInput(profile.phone || ''); setEditingPhone(true); setPhoneError(''); }}
                  style={{fontSize:11,color:'var(--accent)',cursor:'pointer',fontWeight:600}}
                >Edit</span>
              </div>
            ) : (
              <div style={{marginTop:8,padding:'0 16px'}}>
                <input
                  type="tel"
                  value={phoneInput}
                  onChange={e => setPhoneInput(e.target.value)}
                  placeholder="e.g. 0821234567"
                  style={{width:'100%',padding:'8px 12px',borderRadius:8,border:'1.5px solid var(--accent)',background:'var(--bg-input, var(--bg-card))',color:'var(--text-primary)',fontSize:13,fontFamily:'var(--font)',boxSizing:'border-box',textAlign:'center'}}
                />
                {phoneError && <div style={{fontSize:11,color:'var(--red,#ef4444)',marginTop:4}}>{phoneError}</div>}
                <div style={{display:'flex',gap:8,marginTop:8}}>
                  <button
                    onClick={savePhone}
                    disabled={phoneSaving}
                    style={{flex:1,padding:'8px',borderRadius:8,border:'none',background:'var(--accent)',color:'#fff',fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:'var(--font)'}}
                  >{phoneSaving ? 'Saving...' : 'Save'}</button>
                  <button
                    onClick={() => { setEditingPhone(false); setPhoneError(''); }}
                    style={{flex:1,padding:'8px',borderRadius:8,border:'1.5px solid var(--border)',background:'transparent',color:'var(--text-secondary)',fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:'var(--font)'}}
                  >Cancel</button>
                </div>
              </div>
            )}
            <div style={{marginTop:8}}>
              <span className={`badge ${profile.status==='approved'?'badge-approved':'badge-pending'}`}>
                {profile.status==='approved'?'✓ Approved':'⏳ Pending Approval'}
              </span>
            </div>
          </div>
        </div>

        <div className="card" style={{ borderTop: '2px solid var(--border)' }}>
          <div className="card-title" style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-0.2px', marginBottom: 12, paddingBottom: 10, borderBottom: '1px solid var(--border-light)', color: 'var(--text-primary)' }}>My Details</div>
          <div style={{fontSize:12,color:'var(--text-secondary)',lineHeight:1.6,marginBottom:12}}>
            These optional details are shared only with your agency, for event staffing, uniform sizing, and matching you to events where the organiser requires a specific demographic composition. You can remove them at any time.
          </div>
          {!detailsEditing ? (
            <>
              <RoRow label="Height" value={profile.height_cm ? `${profile.height_cm} cm` : ''}/>
              <RoRow label="Weight" value={profile.weight_kg ? `${profile.weight_kg} kg` : ''}/>
              <RoRow label="Race" value={profile.race}/>
              <EditBtn onClick={openDetailsEdit}/>
            </>
          ) : (
            <>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10}}>
            <div>
              <label style={{fontSize:11,fontWeight:600,color:'var(--text-secondary)',display:'block',marginBottom:4}}>Height (cm)</label>
              <input type="number" inputMode="numeric" placeholder="e.g. 175" value={details.height_cm}
                onChange={e => setDetails(d => ({ ...d, height_cm: e.target.value }))}
                style={{width:'100%',padding:'9px 12px',borderRadius:8,border:'1.5px solid var(--border)',background:'var(--bg-input, var(--bg-card))',color:'var(--text-primary)',fontSize:13,fontFamily:'var(--font)',boxSizing:'border-box'}}/>
            </div>
            <div>
              <label style={{fontSize:11,fontWeight:600,color:'var(--text-secondary)',display:'block',marginBottom:4}}>Weight (kg)</label>
              <input type="number" inputMode="numeric" placeholder="e.g. 70" value={details.weight_kg}
                onChange={e => setDetails(d => ({ ...d, weight_kg: e.target.value }))}
                style={{width:'100%',padding:'9px 12px',borderRadius:8,border:'1.5px solid var(--border)',background:'var(--bg-input, var(--bg-card))',color:'var(--text-primary)',fontSize:13,fontFamily:'var(--font)',boxSizing:'border-box'}}/>
            </div>
          </div>
          <div style={{marginBottom:12}}>
            <label style={{fontSize:11,fontWeight:600,color:'var(--text-secondary)',display:'block',marginBottom:4}}>Race</label>
            <select value={details.race} onChange={e => setDetails(d => ({ ...d, race: e.target.value }))}
              style={{width:'100%',padding:'9px 12px',borderRadius:8,border:'1.5px solid var(--border)',background:'var(--bg-input, var(--bg-card))',color:'var(--text-primary)',fontSize:13,fontFamily:'var(--font)',boxSizing:'border-box'}}>
              <option value="">— Select —</option>
              {RACE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div style={{display:'flex',alignItems:'flex-start',gap:10,marginBottom:12,padding:'10px 12px',background:'var(--bg-surface)',border:'1px solid var(--border)',borderRadius:8}}>
            <input type="checkbox" id="profile-consent" checked={consent} onChange={e => setConsent(e.target.checked)}
              style={{marginTop:2,accentColor:'var(--accent)',cursor:'pointer',flexShrink:0,width:16,height:16}}/>
            <label htmlFor="profile-consent" style={{fontSize:12,color:'var(--text-secondary)',lineHeight:1.6,cursor:'pointer'}}>
              I consent to sharing these details with my agency for event staffing, uniform sizing, and matching me to events where the organiser requires a specific demographic composition.
            </label>
          </div>
          {detailsError && <div style={{fontSize:12,color:'var(--red,#ef4444)',marginBottom:10}}>{detailsError}</div>}
          <div style={{display:'flex',gap:8}}>
            <button onClick={saveDetails} disabled={detailsSaving}
              style={{flex:2,padding:'10px',borderRadius:8,border:'none',background:'var(--accent)',color:'#fff',fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:'var(--font)'}}>
              {detailsSaving ? 'Saving...' : 'Save details'}
            </button>
            {(profile.height_cm || profile.weight_kg || profile.race) && (
              <button onClick={cancelDetailsEdit}
                style={{flex:1,padding:'10px',borderRadius:8,border:'1.5px solid var(--border)',background:'transparent',color:'var(--text-secondary)',fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:'var(--font)'}}>
                Cancel
              </button>
            )}
          </div>
            </>
          )}
        </div>

        {/* Session Z: uniform sizing + gender. Separate card from My Details
            because these are NOT consent-gated — no checkbox, own save button. */}
        <div className="card" style={{ borderTop: '2px solid var(--border)' }}>
          <div className="card-title" style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-0.2px', marginBottom: 12, paddingBottom: 10, borderBottom: '1px solid var(--border-light)', color: 'var(--text-primary)' }}>Uniform &amp; Staffing</div>
          <div style={{fontSize:12,color:'var(--text-secondary)',lineHeight:1.6,marginBottom:12}}>
            Used by your agency to order uniforms and plan staffing. You can change or remove these at any time.
          </div>
          {!sizingEditing ? (
            <>
              <RoRow label="Gender" value={profile.gender}/>
              <RoRow label="T-shirt size" value={profile.tshirt_size}/>
              <RoRow label="Pants size" value={profile.pants_size ? String(profile.pants_size) : ''}/>
              <EditBtn onClick={openSizingEdit}/>
            </>
          ) : (
            <>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10}}>
                <div>
                  <label style={{fontSize:11,fontWeight:600,color:'var(--text-secondary)',display:'block',marginBottom:4}}>Gender</label>
                  <select value={sizing.gender} onChange={e => setSizing(d => ({ ...d, gender: e.target.value }))}
                    style={{width:'100%',padding:'9px 12px',borderRadius:8,border:'1.5px solid var(--border)',background:'var(--bg-input, var(--bg-card))',color:'var(--text-primary)',fontSize:13,fontFamily:'var(--font)',boxSizing:'border-box'}}>
                    <option value="">— Select —</option>
                    {GENDER_OPTIONS.map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{fontSize:11,fontWeight:600,color:'var(--text-secondary)',display:'block',marginBottom:4}}>T-shirt size</label>
                  <select value={sizing.tshirt_size} onChange={e => setSizing(d => ({ ...d, tshirt_size: e.target.value }))}
                    style={{width:'100%',padding:'9px 12px',borderRadius:8,border:'1.5px solid var(--border)',background:'var(--bg-input, var(--bg-card))',color:'var(--text-primary)',fontSize:13,fontFamily:'var(--font)',boxSizing:'border-box'}}>
                    <option value="">— Select —</option>
                    {TSHIRT_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div style={{marginBottom:12}}>
                <label style={{fontSize:11,fontWeight:600,color:'var(--text-secondary)',display:'block',marginBottom:4}}>Pants size</label>
                <input type="number" inputMode="numeric" placeholder="e.g. 32" value={sizing.pants_size}
                  onChange={e => setSizing(d => ({ ...d, pants_size: e.target.value }))}
                  style={{width:'100%',padding:'9px 12px',borderRadius:8,border:'1.5px solid var(--border)',background:'var(--bg-input, var(--bg-card))',color:'var(--text-primary)',fontSize:13,fontFamily:'var(--font)',boxSizing:'border-box'}}/>
                <div style={{fontSize:11,color:'var(--text-muted, var(--text-secondary))',marginTop:4,lineHeight:1.5}}>
                  The size you'd ask for in a shop (e.g. 32) — not a centimetre measurement.
                </div>
              </div>
              {sizingError && <div style={{fontSize:12,color:'var(--red,#ef4444)',marginBottom:10}}>{sizingError}</div>}
              <div style={{display:'flex',gap:8}}>
                <button onClick={saveSizing} disabled={sizingSaving}
                  style={{flex:2,padding:'10px',borderRadius:8,border:'none',background:'var(--accent)',color:'#fff',fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:'var(--font)'}}>
                  {sizingSaving ? 'Saving...' : 'Save sizing'}
                </button>
                {(profile.gender || profile.tshirt_size || profile.pants_size) && (
                  <button onClick={cancelSizingEdit}
                    style={{flex:1,padding:'10px',borderRadius:8,border:'1.5px solid var(--border)',background:'transparent',color:'var(--text-secondary)',fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:'var(--font)'}}>
                    Cancel
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        <div className="card" style={{ borderTop: '2px solid var(--border)' }}>
          <div className="card-title" style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-0.2px', marginBottom: 12, paddingBottom: 10, borderBottom: '1px solid var(--border-light)', color: 'var(--text-primary)' }}>Banking Details</div>
          <div style={{fontSize:12,color:'var(--text-secondary)',lineHeight:1.6,marginBottom:12}}>
            Used by your agency to pay you after shifts. Only your agency can see these.
          </div>
          {!bankEditing ? (
            <>
              <RoRow label="Bank" value={profile.bank_name}/>
              <RoRow label="Account holder" value={profile.account_holder}/>
              <RoRow label="Account number" value={profile.account_number}/>
              <RoRow label="Branch code" value={profile.branch_code}/>
              <RoRow label="Account type" value={profile.account_type === 'cheque' ? 'Cheque' : profile.account_type === 'savings' ? 'Savings' : profile.account_type}/>
              <EditBtn onClick={openBankEdit}/>
            </>
          ) : (
            <>
          <div style={{marginBottom:10}}>
            <label style={{fontSize:11,fontWeight:600,color:'var(--text-secondary)',display:'block',marginBottom:4}}>Bank</label>
            <select value={bank.bank_name} onChange={e => setBank(b => ({ ...b, bank_name: e.target.value }))}
              style={{width:'100%',padding:'9px 12px',borderRadius:8,border:'1.5px solid var(--border)',background:'var(--bg-input, var(--bg-card))',color:'var(--text-primary)',fontSize:13,fontFamily:'var(--font)',boxSizing:'border-box'}}>
              <option value="">— Select —</option>
              {SA_BANKS.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div style={{marginBottom:10}}>
            <label style={{fontSize:11,fontWeight:600,color:'var(--text-secondary)',display:'block',marginBottom:4}}>Account holder</label>
            <input placeholder="Name on the account" value={bank.account_holder}
              onChange={e => setBank(b => ({ ...b, account_holder: e.target.value }))}
              style={{width:'100%',padding:'9px 12px',borderRadius:8,border:'1.5px solid var(--border)',background:'var(--bg-input, var(--bg-card))',color:'var(--text-primary)',fontSize:13,fontFamily:'var(--font)',boxSizing:'border-box'}}/>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:12}}>
            <div>
              <label style={{fontSize:11,fontWeight:600,color:'var(--text-secondary)',display:'block',marginBottom:4}}>Account number</label>
              <input inputMode="numeric" placeholder="6-13 digits" maxLength={13} value={bank.account_number}
                onChange={e => setBank(b => ({ ...b, account_number: e.target.value.replace(/\D/g, '') }))}
                style={{width:'100%',padding:'9px 12px',borderRadius:8,border:'1.5px solid var(--border)',background:'var(--bg-input, var(--bg-card))',color:'var(--text-primary)',fontSize:13,fontFamily:'var(--font)',boxSizing:'border-box'}}/>
            </div>
            <div>
              <label style={{fontSize:11,fontWeight:600,color:'var(--text-secondary)',display:'block',marginBottom:4}}>Branch code</label>
              <input inputMode="numeric" placeholder="6 digits" maxLength={6} value={bank.branch_code}
                onChange={e => setBank(b => ({ ...b, branch_code: e.target.value.replace(/\D/g, '') }))}
                style={{width:'100%',padding:'9px 12px',borderRadius:8,border:'1.5px solid var(--border)',background:'var(--bg-input, var(--bg-card))',color:'var(--text-primary)',fontSize:13,fontFamily:'var(--font)',boxSizing:'border-box'}}/>
            </div>
          </div>
          <div style={{marginBottom:12}}>
            <label style={{fontSize:11,fontWeight:600,color:'var(--text-secondary)',display:'block',marginBottom:6}}>Account type</label>
            <div style={{display:'flex',gap:8}}>
              {['cheque', 'savings'].map(t => (
                <button key={t} type="button" onClick={() => setBank(b => ({ ...b, account_type: t }))}
                  style={{flex:1,padding:'9px',borderRadius:8,fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:'var(--font)',
                    border: bank.account_type === t ? '1.5px solid var(--accent)' : '1.5px solid var(--border)',
                    background: bank.account_type === t ? 'var(--accent)' : 'transparent',
                    color: bank.account_type === t ? '#fff' : 'var(--text-secondary)'}}>
                  {t === 'cheque' ? 'Cheque' : 'Savings'}
                </button>
              ))}
            </div>
          </div>
          {bankError && <div style={{fontSize:12,color:'var(--red,#ef4444)',marginBottom:10}}>{bankError}</div>}
          <div style={{display:'flex',gap:8}}>
            <button onClick={saveBank} disabled={bankSaving}
              style={{flex:2,padding:'10px',borderRadius:8,border:'none',background:'var(--accent)',color:'#fff',fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:'var(--font)'}}>
              {bankSaving ? 'Saving...' : 'Save banking details'}
            </button>
            {(profile.bank_name || profile.account_holder || profile.account_number || profile.branch_code || profile.account_type) && (
              <button onClick={cancelBankEdit}
                style={{flex:1,padding:'10px',borderRadius:8,border:'1.5px solid var(--border)',background:'transparent',color:'var(--text-secondary)',fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:'var(--font)'}}>
                Cancel
              </button>
            )}
          </div>
            </>
          )}
        </div>

        <div className="card" style={{ borderTop: '2px solid var(--border)' }}>
          <div className="card-title" style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-0.2px', marginBottom: 12, paddingBottom: 10, borderBottom: '1px solid var(--border-light)', color: 'var(--text-primary)' }}>Performance</div>
          <div className="stats-row" style={{margin:0,gridTemplateColumns:'repeat(2,1fr)'}}>
            <div className="stat-box"><div className="stat-value">{profile.shifts_completed}</div><div className="stat-label">Shifts done</div></div>
            <div className="stat-box"><div className="stat-value accent">{Number(profile.reliability_score).toFixed(1)}</div><div className="stat-label">Avg rating</div></div>
          </div>
          <div style={{marginTop:12}}><Stars score={profile.reliability_score}/></div>
        </div>

        <div className="card" style={{ borderTop: '2px solid var(--border)' }}>
          <div className="card-title" style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-0.2px', marginBottom: 12, paddingBottom: 10, borderBottom: '1px solid var(--border-light)', color: 'var(--text-primary)' }}>My Skills</div>
          <div style={{fontSize:13,color:'var(--text-secondary)'}}>
            {profile.skills ? profile.skills.split(',').map(s=>s.trim()).filter(Boolean).map(s=>(
              <span key={s} className="badge badge-info" style={{marginRight:6,marginBottom:6,display:'inline-block'}}>{s}</span>
            )) : <span style={{color:'var(--text-muted)'}}>No skills assigned yet — contact your agency.</span>}
          </div>
        </div>

        <div className="card" style={{ borderTop: '2px solid var(--border)' }}>
          <div className="card-title" style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-0.2px', marginBottom: 12, paddingBottom: 10, borderBottom: '1px solid var(--border-light)', color: 'var(--text-primary)' }}>Display</div>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
            <div>
              <div style={{fontSize:14,fontWeight:600,color:'var(--text-primary)'}}>{dark?'Dark mode':'Light mode'}</div>
              <div style={{fontSize:12,color:'var(--text-secondary)',marginTop:2}}>Tap to switch</div>
            </div>
            <div className={`toggle ${dark?'on':''}`} onClick={toggle}/>
          </div>
        </div>

        <div className="card" style={{ borderTop: '2px solid var(--border)' }}>
          <div className="card-title" style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-0.2px', marginBottom: 12, paddingBottom: 10, borderBottom: '1px solid var(--border-light)', color: 'var(--text-primary)' }}>Notifications</div>
          {/* Session AH. The toggle is only rendered when it can actually do
              something — the other three states get an explanation instead of
              a control that would fail silently when tapped. */}
          {pushState === 'unsupported' ? (
            <div style={{fontSize:12,color:'var(--text-secondary)',lineHeight:1.6}}>
              This browser doesn't support notifications.
            </div>
          ) : pushState === 'needs-install' ? (
            <div style={{fontSize:12,color:'var(--text-secondary)',lineHeight:1.6}}>
              To get notifications on iPhone, add the app to your Home Screen first — tap the <strong>Share button</strong> (box with arrow), then <strong>"Add to Home Screen"</strong>. Open it from there and this option will appear.
            </div>
          ) : pushState === 'denied' ? (
            <div style={{fontSize:12,color:'var(--text-secondary)',lineHeight:1.6}}>
              Notifications are blocked for this app. You can turn them back on in your phone's settings.
            </div>
          ) : (
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <div>
                <div style={{fontSize:14,fontWeight:600,color:'var(--text-primary)'}}>
                  {pushState === 'on' ? 'Notifications on' : 'Notifications off'}
                </div>
                <div style={{fontSize:12,color:'var(--text-secondary)',marginTop:2}}>
                  {pushBusy ? 'Working...' : 'Hear about new shifts as they\u2019re posted'}
                </div>
              </div>
              <div
                className={`toggle ${pushState === 'on' ? 'on' : ''}`}
                onClick={async () => {
                  if (pushBusy) return;
                  setPushBusy(true); setPushError('');
                  try {
                    if (pushState === 'on') {
                      await unsubscribeFromPush();
                    } else {
                      const r = await subscribeToPush();
                      if (!r.ok) {
                        setPushError(
                          r.reason === 'denied'
                            ? 'Notifications are blocked. Turn them on in your phone settings.'
                            : r.reason === 'server-disabled'
                              ? 'Notifications aren\u2019t available yet. Please tell your coordinator.'
                              : 'Could not turn notifications on. Please try again.'
                        );
                      }
                    }
                  } catch {
                    setPushError('Could not change notifications. Please try again.');
                  } finally {
                    setPushState(await getPushState());
                    setPushBusy(false);
                  }
                }}
              />
            </div>
          )}
          {pushError && <p style={{color:'var(--danger)',fontSize:12,marginTop:10,marginBottom:0}}>{pushError}</p>}
        </div>

        <div className="card" style={{ borderTop: '2px solid var(--border)' }}>
          <div className="card-title" style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-0.2px', marginBottom: 12, paddingBottom: 10, borderBottom: '1px solid var(--border-light)', color: 'var(--text-primary)' }}>Legal</div>
          {/* Session W (P3): version-stamped filenames matching the
              terms_version / privacy_version recorded in consent_log.
              Unversioned names meant swapping a PDF retroactively changed the
              document every past consent record claimed to cover. A revision
              ships as a new file. Session X: the version lives in
              utils/consent.js, kept in sync with server.js. */}
          <div style={{display:'flex',flexDirection:'column',gap:10}}>
            <a href={TERMS_PDF} onClick={e => { e.preventDefault(); window.open(TERMS_PDF, '_blank', 'noopener,noreferrer'); }} style={{display:'flex',alignItems:'center',justifyContent:'space-between',fontSize:14,color:'var(--text-primary)',textDecoration:'none',padding:'4px 0',cursor:'pointer'}}>
              <span>Terms &amp; Conditions</span>
              <span style={{fontSize:12,color:'var(--text-secondary)'}}>↗</span>
            </a>
            <a href={PRIVACY_PDF} onClick={e => { e.preventDefault(); window.open(PRIVACY_PDF, '_blank', 'noopener,noreferrer'); }} style={{display:'flex',alignItems:'center',justifyContent:'space-between',fontSize:14,color:'var(--text-primary)',textDecoration:'none',padding:'4px 0',cursor:'pointer'}}>
              <span>Privacy Policy</span>
              <span style={{fontSize:12,color:'var(--text-secondary)'}}>↗</span>
            </a>
          </div>
        </div>

        {/* Session T: its own card, below Legal, so the agency's commercial terms
            are never mistaken for the platform's Responsible Party documents.
            window.open rather than a plain target="_blank": inside an installed
            PWA an anchor to an external URL can open in the same webview with no
            way back, which would trap the user. Matches the two links above. */}
        {AGENCY_TERMS_URL && (
          <div className="card" style={{ borderTop: '2px solid var(--border)' }}>
            <div className="card-title" style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-0.2px', marginBottom: 12, paddingBottom: 10, borderBottom: '1px solid var(--border-light)', color: 'var(--text-primary)' }}>Staffing Terms</div>
            <a href={AGENCY_TERMS_URL} onClick={e => { e.preventDefault(); window.open(AGENCY_TERMS_URL, '_blank', 'noopener,noreferrer'); }} style={{display:'flex',alignItems:'center',justifyContent:'space-between',fontSize:14,color:'var(--text-primary)',textDecoration:'none',padding:'4px 0',cursor:'pointer'}}>
              <span>View {BRAND} staffing terms</span>
              <span style={{fontSize:12,color:'var(--text-secondary)'}}>↗</span>
            </a>
            <div style={{fontSize:11,color:'var(--text-muted)',lineHeight:1.5,marginTop:10}}>
              Your agency's terms of engagement — separate from the platform Terms &amp; Conditions above.
            </div>
          </div>
        )}

        <div style={{padding:'4px 16px'}}>
          <p style={{fontSize:11,color:'var(--text-muted)',lineHeight:1.5,marginBottom:12}}>
            *Keep your details accurate for the best opportunities.*
          </p>
        </div>
        {saved&&<div style={{margin:'0 16px 12px',padding:12,background:'rgba(22,163,74,0.1)',border:'1px solid rgba(22,163,74,0.3)',borderRadius:8,fontSize:13,color:'var(--green)',textAlign:'center'}}>✓ Updated</div>}

        <div style={{padding:'8px 16px 32px'}}>
          <button
            onClick={logout}
            style={{width:'100%',padding:'13px 24px',borderRadius:8,border:'1.5px solid var(--accent)',background:'transparent',color:'var(--accent)',fontSize:14,fontWeight:700,cursor:'pointer',fontFamily:'var(--font)',letterSpacing:'0.2px'}}
          >
            Sign out
          </button>
        </div>
      </div>


      <BottomNav/>
    </div>
  );
}
